// src/components/album/GallerySidestrip.jsx
import React, { useState } from 'react';
import { 
  Grid, 
  Image as ImageIcon, 
  PanelLeft, 
  PanelRight, 
  ChevronLeft, 
  ChevronRight,
  X
} from 'lucide-react';

export const GallerySidestrip = ({ 
  galleries, 
  bookData,
  activeGalleryId, 
  onGalleryChange, 
  settings, 
  customStyling,
  headerHeight = 80, 
  className = '',
  isCollapsed = false,      
  onToggleCollapse, 
}) => {
  const {
    position = 'right',
    showThumbnails = true,
    showLabels = true,
    showCounts = false,
    width = 'medium',
    thumbnailSize = 'medium',
    collapsible = true
  } = settings || {};

  // Width configurations
  const widthClasses = {
    narrow: isCollapsed ? 'w-0' : 'w-16',
    medium: isCollapsed ? 'w-0' : 'w-20',
    wide: isCollapsed ? 'w-0' : 'w-24'
  };

  const thumbnailSizes = {
    small: 'w-10 h-10',
    medium: 'w-12 h-12',
    large: 'w-14 h-14'
  };

  // Position classes
  const positionClasses = position === 'left' 
    ? 'left-0 border-r' 
    : 'right-0 border-l';

  // Calculate dynamic top position based on header height
  const topPosition = `${headerHeight}px `;
  const stripHeight = `calc(100vh - ${headerHeight}px)`;

  // Collapse button rendering
  if (isCollapsed && collapsible) {
    return (
      <div 
        className={`fixed ${position === 'left' ? 'left-0' : 'right-0'} z-30 ${className}`}
        style={{ top: topPosition }}
      >
        <button
          onClick={() => onToggleCollapse(false)}
          className="mt-32 p-3 bg-white/40 shadow-2xl hover:bg-gray-50 transition-all duration-200 rounded-r-lg group"
          style={{ 
            borderColor: customStyling.primaryColor + '40',
            borderWidth: '1px',
            borderLeft: position === 'right' ? 0 : undefined,
            borderRight: position === 'left' ? 0 : undefined
          }}
          title="Show gallery navigation"
        >
          {position === 'left' ? (
            <ChevronRight 
              className="h-5 w-5 transition-transform group-hover:scale-110" 
              style={{ color: customStyling.primaryColor }} 
            />
          ) : (
            <ChevronLeft 
              className="h-5 w-5 transition-transform group-hover:scale-110" 
              style={{ color: customStyling.primaryColor }} 
            />
          )}
        </button>
      </div>
    );
  }

  // Calculate total items across all galleries
  const totalItems = galleries.reduce((sum, g) => sum + (g.mediaItems?.length || 0), 0);

  return (
    <div 
      className={`fixed ${positionClasses} ${widthClasses[width]} z-30 transition-all duration-300 ${className}`}
      style={{ 
        top: topPosition,
        height: stripHeight,
        backgroundColor: customStyling.gallerySidebarBg || '#ffffff',
        borderColor: customStyling.primaryColor + '20',
        boxShadow: `0 6px 8px ${customStyling.gallerySidebarShadow || '#000000'}10, 0 2px 4px -1px ${customStyling.gallerySidebarShadow || '#000000'}06`,
        overflow: 'hidden',
        zIndex: 30
      }}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div 
          className="px-2 py-3 border-b flex items-center justify-between flex-shrink-0"
          style={{ borderColor: customStyling.primaryColor + '20' }}
        >
          <h3 
            className="font-semibold text-xs truncate" 
            style={{ color: customStyling.textColor || '#333' }}
          >
            Galleries
          </h3>
          {collapsible && (
            <button
              onClick={() => onToggleCollapse(true)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              title="Hide gallery navigation"
            >
              {position === 'left' ? (
                <ChevronLeft className="h-5 w-5" style={{ color: customStyling.primaryColor }} />
              ) : (
                <ChevronRight className="h-5 w-5" style={{ color: customStyling.primaryColor }} />
              )}
            </button>
          )}
        </div>

        {/* Scrollable Gallery List */}
        <div className="flex-1 overflow-y-auto py-2 px-1.5 space-y-1.5 custom-scrollbar">
          {/* All Galleries Option */}
          <button
            onClick={() => onGalleryChange('all')}
            className={`w-full rounded-lg transition-all hover:shadow-md ${
              activeGalleryId === 'all'
                ? 'ring-2 shadow-md scale-105'
                : 'hover:bg-gray-50 hover:scale-102'
            }`}
            style={{
              ringColor: activeGalleryId === 'all' ? customStyling.primaryColor : undefined,
              backgroundColor: activeGalleryId === 'all' ? customStyling.primaryColor + '10' : undefined
            }}
          >
            <div className="p-1.5 flex flex-col items-center gap-1.5">
              {showThumbnails && (
                <div 
                  className={`${thumbnailSizes[thumbnailSize]} rounded-lg overflow-hidden flex items-center justify-center shadow-md bg-white`}
                >
                  {bookData.favicon || bookData.faviconUrl ? (
                    <img
                      src={bookData.favicon || bookData.faviconUrl}
                      alt="Album logo"
                      className="w-full h-full object-contain p-1"
                    />
                  ) : (
                    <div 
                      className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center"
                    >
                      <Grid className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
              )}
              {showLabels && (
                <span 
                  className="text-[10px] font-semibold text-center line-clamp-2 px-0.5"
                  style={{ 
                    color: activeGalleryId === 'all' ? customStyling.primaryColor : customStyling.textColor 
                  }}
                >
                  All Galleries
                </span>
              )}
              {showCounts && (
                <span 
                  className="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: activeGalleryId === 'all' 
                      ? customStyling.primaryColor + '20' 
                      : '#e5e7eb',
                    color: activeGalleryId === 'all' 
                      ? customStyling.primaryColor 
                      : '#6b7280'
                  }}
                >
                  {totalItems}
                </span>
              )}
            </div>
          </button>

          {/* Divider */}
          <div className="border-t my-1.5" style={{ borderColor: customStyling.primaryColor + '20' }} />

          {/* Individual Galleries */}
          {galleries.map((gallery) => {
            const itemCount = gallery.mediaItems?.length || 0;
            const isActive = activeGalleryId === gallery.id;
            
            return (
              <button
                key={gallery.id}
                onClick={() => onGalleryChange(gallery.id)}
                className={`w-full rounded-lg transition-all hover:shadow-md ${
                  isActive
                    ? 'ring-2 shadow-md scale-105'
                    : 'hover:bg-gray-50 hover:scale-102'
                }`}
                style={{
                  ringColor: isActive ? customStyling.primaryColor : undefined,
                  backgroundColor: isActive ? customStyling.primaryColor + '10' : undefined
                }}
                title={gallery.name}
              >
                <div className="p-1.5 flex flex-col items-center gap-1.5">
                  {showThumbnails && (
                    <div 
                      className={`${thumbnailSizes[thumbnailSize]} rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 shadow-sm`}
                    >
                      {gallery.coverImage || gallery.mediaItems?.[0]?.thumbnailUrl || gallery.mediaItems?.[0]?.url ? (
                        <img
                          src={gallery.coverImage || gallery.mediaItems[0]?.thumbnailUrl || gallery.mediaItems[0]?.url}
                          alt={gallery.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                          <ImageIcon className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                  )}
                  {showLabels && (
                    <span 
                      className="text-[10px] font-semibold text-center line-clamp-2 px-0.5"
                      style={{ 
                        color: isActive ? customStyling.primaryColor : customStyling.textColor 
                      }}
                    >
                      {gallery.name}
                    </span>
                  )}
                  {showCounts && (
                    <span 
                      className="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: isActive 
                          ? customStyling.primaryColor + '20' 
                          : '#e5e7eb',
                        color: isActive 
                          ? customStyling.primaryColor 
                          : '#6b7280'
                      }}
                    >
                      {itemCount}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer with gallery count */}
        <div 
          className="px-2 py-1.5 border-t text-center flex-shrink-0"
          style={{ borderColor: customStyling.primaryColor + '20' }}
        >
          <span className="text-[10px] text-gray-500">
            {galleries.length} {galleries.length === 1 ? 'gallery' : 'galleries'}
          </span>
        </div>
      </div>

      {/* Enhanced Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${customStyling.primaryColor}10 transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 2px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          margin: 8px 0;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(
            to bottom,
            ${customStyling.primaryColor}20,
            ${customStyling.primaryColor}40,
            ${customStyling.primaryColor}20
          );
          border-radius: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(
            to bottom,
            ${customStyling.primaryColor}40,
            ${customStyling.primaryColor}60,
            ${customStyling.primaryColor}40
          );
          width: 4px;
        }
        
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: linear-gradient(
            to bottom,
            ${customStyling.primaryColor}30,
            ${customStyling.primaryColor}50,
            ${customStyling.primaryColor}30
          );
        }
  
  /* Smooth scroll behavior */
      .custom-scrollbar {
        scroll-behavior: smooth;
      }
      
      /* Hide scrollbar when not hovering the container */
      .custom-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
        background: transparent;
      }
      
      /* Show scrollbar on hover with smooth transition */
      .custom-scrollbar:hover::-webkit-scrollbar-thumb {
        background: linear-gradient(
          to bottom,
          ${customStyling.primaryColor}25,
          ${customStyling.primaryColor}45,
          ${customStyling.primaryColor}25
        );
      }
    `}</style>
    </div>
  );
};